import React, { useMemo } from 'react';
import { 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import { ProjectUnlockData } from '@/types/project';
import { formatCompactNumber } from '@/utils';

interface UnlockTimelineChartProps {
  unlockData: ProjectUnlockData;
}

interface TimelineDataPoint {
  date: string;
  cumulativeUnlocked: number;
  cumulativeLocked: number;
  totalSupply: number;
}

interface TooltipPayloadItem {
  payload: TimelineDataPoint;
}

const UnlockTimelineChart: React.FC<UnlockTimelineChartProps> = ({ unlockData }) => {
  // Generate timeline data based on real unlock data
  const timelineData = useMemo(() => {
    const dataPoints: TimelineDataPoint[] = [];
    const startDate = new Date(unlockData.vesting_start_date);
    const endDate = new Date(unlockData.vesting_end_date);
    
    const current = new Date(startDate);
    const end = new Date(endDate);
    
    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      
      const totalDays = Math.floor((end.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysSinceStart = Math.floor((current.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const progress = Math.min(Math.max(daysSinceStart / totalDays, 0), 1);
      
      const currentUnlocked = unlockData.total_unlocked + (unlockData.total_locked * progress);
      const currentLocked = unlockData.total_supply - currentUnlocked;
      
      dataPoints.push({
        date: dateStr,
        cumulativeUnlocked: Math.min(currentUnlocked, unlockData.total_supply),
        cumulativeLocked: Math.max(currentLocked, 0),
        totalSupply: unlockData.total_supply
      });
      
      current.setMonth(current.getMonth() + 3); // Quarterly points
    }
    
    return dataPoints;
  }, [unlockData]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const formatCurrency = (value: number) => {
    if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return value.toString();
  };

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: TooltipPayloadItem[]; label?: string }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-[#1a1a1a] p-3 border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] shadow-lg">
          <p className="font-['Plus_Jakarta_Sans'] font-medium text-[#2d2923] dark:text-[#fdfdfd] mb-2">
            {label ? formatDate(label) : ''}
          </p>
          <div className="space-y-1">
            <p className="font-['Plus_Jakarta_Sans'] text-[13px]">
              <span className="text-[#a9c804]">Unlocked:</span>{' '}
              <span className="text-[#2d2923] dark:text-[#fdfdfd]">
                {formatCompactNumber(data.cumulativeUnlocked)}
              </span>
            </p>
            <p className="font-['Plus_Jakarta_Sans'] text-[13px]">
              <span className="text-[#ee9d1a]">Locked:</span>{' '}
              <span className="text-[#2d2923] dark:text-[#fdfdfd]">
                {formatCompactNumber(data.cumulativeLocked)}
              </span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  if (timelineData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="font-['Plus_Jakarta_Sans'] text-[rgba(45,41,35,0.64)] dark:text-[rgba(253,253,253,0.64)]">
          No timeline data available
        </p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={timelineData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
        <XAxis 
          dataKey="date" 
          tick={{ 
            fontSize: 11, 
            fill: "#878787",
            fontFamily: "Plus Jakarta Sans"
          }}
          tickFormatter={formatDate}
          stroke="transparent"
          axisLine={false}
          tickLine={false}
        />
        <YAxis 
          tick={{ 
            fontSize: 13, 
            fill: "#878787",
            fontFamily: "Plus Jakarta Sans"
          }}
          tickFormatter={formatCurrency}
          stroke="transparent"
          axisLine={false}
          tickLine={false}
          width={60}
        />
        <Tooltip content={<CustomTooltip />} />
        
        {/* Unlocked tokens area - green */}
        <Area
          type="monotone"
          dataKey="cumulativeUnlocked"
          stackId="1"
          stroke="#a9c804"
          fill="#a9c804"
          fillOpacity={0.7}
          name="Unlocked"
        />
        
        {/* Locked tokens area - orange */}
        <Area
          type="monotone"
          dataKey="cumulativeLocked"
          stackId="1"
          stroke="#ee9d1a"
          fill="#ee9d1a"
          fillOpacity={0.7}
          name="Locked"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default UnlockTimelineChart;
